import React, { useState, useEffect, useRef } from 'react';

const SimonSaysGame = ({ gameData, onComplete }) => {
    const [currentRoundIndex, setCurrentRoundIndex] = useState(0);
    const [score, setScore] = useState(0);
    const [feedback, setFeedback] = useState(null);
    const [gameState, setGameState] = useState('listening'); // 'listening', 'waiting', 'result'
    const [timer, setTimer] = useState(5);
    const [voiceWarning, setVoiceWarning] = useState(false);

    const [englishVoice, setEnglishVoice] = useState(null);
    const synthRef = useRef(window.speechSynthesis);

    const currentRound = gameData?.rounds?.[currentRoundIndex];

    useEffect(() => {
        const loadVoices = () => {
            const voices = synthRef.current.getVoices();
            const goodVoice = voices.find(voice => voice.lang.includes('en') && voice.name.includes('Google'));
            const anyEnVoice = voices.find(voice => voice.lang.includes('en'));

            if (goodVoice) {
                setEnglishVoice(goodVoice);
                setVoiceWarning(false);
            } else {
                if (anyEnVoice) setEnglishVoice(anyEnVoice);
                setVoiceWarning(true); 
            }
        };

        if (synthRef.current.getVoices().length > 0) {
            loadVoices();
        } else {
            synthRef.current.onvoiceschanged = loadVoices;
        }

        return () => {
            synthRef.current.onvoiceschanged = null;
        };
    }, []);

    useEffect(() => {
        if (gameState === 'listening' && currentRound) {
            speakCommand(currentRound.text);
            const showTime = 4000;
            const timerId = setTimeout(() => {
                setGameState('waiting');
                setTimer(5);
            }, showTime);
            return () => clearTimeout(timerId);
        }
    }, [currentRoundIndex, gameState, currentRound]);

    useEffect(() => {
        let timerId;
        if (gameState === 'waiting' && timer > 0) {
            timerId = setTimeout(() => {
                setTimer(timer - 1);
                // Subtle rhythmic pulse for the timer
                if (navigator.vibrate) navigator.vibrate(20);
            }, 1000);
        } else if (gameState === 'waiting' && timer === 0) {
            handleTimeUp();
        }
        return () => clearTimeout(timerId);
    }, [gameState, timer]);

    const speakCommand = (text) => {
        if ('speechSynthesis' in window) {
            synthRef.current.cancel(); 
            const utterance = new SpeechSynthesisUtterance(text);
            utterance.lang = 'en-US'; 
            utterance.rate = 0.85; 
            utterance.pitch = 1.1; 

            if (englishVoice) {
                utterance.voice = englishVoice;
            }

            synthRef.current.speak(utterance);
            if (navigator.vibrate) navigator.vibrate(50);
        }
    };

    const handleAction = () => {
        if (gameState !== 'waiting') return;
        if (navigator.vibrate) navigator.vibrate(60);
        if (currentRound.simon_says) {
            handleSuccess("TIKSLU! Tu paklausei Simono! 👑");
        } else {
            handleFailure("OI! Simonas šito nesakė! 🛑");
        }
    };

    const handleTimeUp = () => {
        if (currentRound.simon_says) {
            handleFailure("LAIKAS BAIGĖSI! Simonas sakė tai padaryti. ⏳");
        } else {
            handleSuccess("ŠAUNUOLIS! Tu likai vietoje. ✨");
        }
    };

    const handleSuccess = (msg) => {
        const pointsToAdd = gameData.points_per_round || 10;
        const newScore = score + pointsToAdd;
        setScore(newScore);
        setFeedback({ type: 'correct', message: msg });
        setGameState('result');
        if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
        setTimeout(nextRound, 3000);
    };

    const handleFailure = (msg) => {
        setFeedback({ type: 'wrong', message: msg });
        setGameState('result');
        if (navigator.vibrate) navigator.vibrate([50, 50, 50]);
        setTimeout(nextRound, 3000);
    };

    const nextRound = () => {
        if (currentRoundIndex < gameData.rounds.length - 1) {
            setCurrentRoundIndex(prev => prev + 1);
            setFeedback(null);
            setGameState('listening');
            if (navigator.vibrate) navigator.vibrate(30);
        } else {
            if (navigator.vibrate) navigator.vibrate([200, 100, 200, 100, 300]);
            onComplete?.(score);
        }
    };

    if (!gameData || !gameData.rounds || !currentRound) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-amber-100 animate-pulse bg-white">
                <div className="text-lg md:text-4xl md:text-[100px] mb-8">👸</div>
                <h3 className="text-base md:text-3xl font-black text-amber-900 tracking-tighter uppercase">Simonas ruošiasi...</h3>
            </div>
        );
    }

    const progress = (currentRoundIndex / gameData.rounds.length) * 100;

    return (
        <div className="space-y-3 md:space-y-8 animate-slide-up select-none">
            {/* Header / Command HUD */}
            <div className="premium-glass rounded-lg md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-amber-50">
                <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/5 rounded-full blur-[80px] pointer-events-none"></div>
                <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                    <div className="flex items-center gap-2 md:p-6">
                        <div className="w-20 h-20 bg-amber-100 rounded-3xl flex items-center justify-center text-lg md:text-4xl shadow-inner border-2 border-amber-200 animate-float">
                            🤴
                        </div>
                        <div>
                            <h3 className="text-lg md:text-4xl font-black text-slate-800 tracking-tighter leading-tight uppercase">
                                Simonas <span className="text-amber-600">Sako</span>
                            </h3>
                            <p className="text-slate-500 font-bold italic font-serif">
                                Klausykis atidžiai ir daryk tik tai, ką liepia karalius!
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 md:p-6">
                        <div className="bg-white/50 px-8 py-4 rounded-[2rem] border-2 border-white shadow-inner">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-2">Garbė</p>
                            <p className="text-base md:text-3xl font-black text-amber-600 leading-none">⭐ {score}</p>
                        </div>
                        <div className="bg-white/50 px-8 py-4 rounded-[2rem] border-2 border-white shadow-inner text-center min-w-[120px]">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-2">Raundas</p>
                            <p className="text-base md:text-3xl font-black text-slate-800 leading-none">{currentRoundIndex + 1}/{gameData.rounds.length}</p>
                        </div>
                    </div>
                </div>

                <div className="mt-8 w-full bg-slate-200/50 rounded-full h-4 p-1 shadow-inner overflow-hidden border-2 border-slate-300">
                    <div 
                        className="h-full bg-gradient-to-r from-amber-400 via-orange-500 to-yellow-600 rounded-full transition-all duration-1000 shadow-[0_0_20px_rgba(245,158,11,0.4)]"
                        style={{ width: `${progress}%` }}
                    />
                </div>
            </div>

            {/* Main Command Stage Interface */}
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-10 lg:p-4 md:p-16 shadow-2xl border-white/60 bg-white/40 backdrop-blur-xl relative overflow-hidden flex flex-col items-center min-h-[50vh] justify-center text-center">
                <div className="absolute top-0 right-0 p-4 md:p-12 text-2xl md:text-6xl md:text-[200px] opacity-[0.03] select-none pointer-events-none rotate-12">📢</div>
                <div className="absolute bottom-0 left-0 p-4 md:p-12 text-2xl md:text-6xl md:text-[200px] opacity-[0.03] select-none pointer-events-none -rotate-12 text-amber-600">🤴</div>
                
                <div className="w-full max-w-5xl space-y-16 relative z-10">
                    {/* High-End Command Display */}
                    <div className="space-y-4 md:space-y-10">
                        <div className={`text-2xl md:text-6xl md:text-[200px] transition-all duration-700 ease-in-out drop-shadow-[0_40px_80px_rgba(245,158,11,0.4)] ${gameState === 'listening' ? 'animate-pulse grayscale-50 scale-105' : 'animate-float scale-110'}`}>
                            {currentRound.emoji}
                        </div>
                        
                        <div className="space-y-6">
                            <div className="bg-slate-900 text-white rounded-[3.5rem] p-4 md:p-12 lg:p-4 md:p-16 shadow-2xl border-[8px] border-slate-800 relative group">
                                <div className="absolute top-0 left-0 p-3 md:p-8 text-2xl md:text-6xl opacity-10 select-none pointer-events-none group-hover:scale-125 transition-transform">🔊</div>
                                <h2 className={`text-xl md:text-5xl lg:text-7xl font-black tracking-tighter leading-tight italic font-serif transition-colors duration-500 ${currentRound.simon_says ? "text-amber-400 underline decoration-amber-400/30 decoration-8 underline-offset-8" : "text-white"}`}>
                                    "{currentRound.text}"
                                </h2>
                                
                                {gameState === 'listening' && (
                                    <div className="mt-8 flex items-center justify-center gap-4 text-amber-400/60 font-black uppercase tracking-[0.4em] animate-pulse">
                                        <div className="w-3 h-3 bg-amber-400 rounded-full animate-bounce" />
                                        <div className="w-3 h-3 bg-amber-400 rounded-full animate-bounce [animation-delay:0.2s]" />
                                        <div className="w-3 h-3 bg-amber-400 rounded-full animate-bounce [animation-delay:0.4s]" />
                                        <span className="text-xl">Simonas kalba...</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Interactive Decision Area */}
                    <div className="pt-10">
                        {gameState === 'waiting' && (
                            <div className="flex flex-col items-center space-y-4 md:space-y-12 animate-pop-in">
                                <div className="flex items-center gap-3 md:p-8 bg-white/80 px-10 py-5 rounded-lg md:rounded-[2.5rem] border-4 border-amber-100 shadow-xl relative overflow-hidden">
                                    <div className="absolute inset-0 bg-rose-500/5 animate-pulse" />
                                    <div className="w-20 h-20 rounded-full bg-rose-500 border-4 border-white flex items-center justify-center text-lg md:text-4xl font-black text-white shadow-2xl relative z-10">
                                        {timer}
                                    </div>
                                    <div className="text-2xl font-black text-slate-800 uppercase tracking-widest relative z-10">Sekundės apsispręsti!</div>
                                </div>

                                <button
                                    onClick={handleAction}
                                    className="premium-button-3d group w-full max-w-2xl py-12 bg-gradient-to-r from-emerald-500 via-green-500 to-teal-600 text-white rounded-[3.5rem] font-black text-lg md:text-4xl shadow-[0_30px_60px_rgba(16,185,129,0.3)] transition-all border-b-[12px] border-emerald-800 uppercase tracking-[0.2em] relative overflow-hidden"
                                >
                                    <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                                    <span className="relative z-10 flex items-center justify-center gap-2 md:p-6">
                                        ATLIKTI VEIKSMĄ! <span className="text-xl md:text-5xl animate-bounce">💪</span>
                                    </span>
                                </button>
                                
                                <div className="bg-slate-100/50 px-10 py-4 rounded-full border-2 border-slate-200 shadow-inner">
                                    <p className="text-slate-500 font-black text-sm uppercase tracking-[0.4em]">Jei Simonas nesakė - tiesiog lauk!</p>
                                </div>
                            </div>
                        )}
                        
                        {gameState === 'listening' && (
                            <div className="p-4 md:p-12 rounded-[3.5rem] border-4 border-dashed border-slate-200 bg-slate-50/50">
                                <p className="text-base md:text-3xl font-black text-slate-400 uppercase tracking-widest italic animate-pulse">Atidžiai klausykis nurodymo...</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Immersive Feedback Overlay */}
            {gameState === 'result' && feedback && (
                <div className="fixed inset-0 flex items-center justify-center z-[1000] p-2 md:p-6">
                    <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-2xl animate-fade-in" />
                    <div className={`
                        max-w-3xl w-full p-4 md:p-20 rounded-3xl md:rounded-[5rem] bg-white shadow-[0_60px_120px_rgba(0,0,0,0.6)] relative animate-pop-in border-[16px]
                        ${feedback.type === 'correct' ? 'border-emerald-500' : 'border-rose-500'}
                    `}>
                        <div className="absolute -top-4 md:p-20 left-1/2 -translate-x-1/2 w-40 h-40 bg-white rounded-full flex items-center justify-center text-8xl shadow-2xl border-8 border-inherit animate-bounce">
                            {feedback.type === 'correct' ? '🏆' : '⚠️'}
                        </div>
                        
                        <div className="text-center space-y-4 md:space-y-12 pt-10">
                            <h2 className={`text-7xl font-black uppercase tracking-tighter leading-none italic font-serif ${feedback.type === 'correct' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {feedback.type === 'correct' ? 'KARALIŠKA!' : 'OJEI, KLAIDA!'}
                            </h2>

                            <div className={`p-4 md:p-12 rounded-2xl md:rounded-[4rem] border-4 text-center italic font-serif leading-relaxed ${feedback.type === 'correct' ? 'bg-emerald-50 border-emerald-100' : 'bg-rose-50 border-rose-100'}`}>
                                <p className="text-lg md:text-4xl font-bold text-slate-700 leading-relaxed italic">
                                    {feedback.message}
                                </p>
                            </div>

                            <div className="flex items-center justify-center gap-2 md:p-6">
                                <div className="w-5 h-5 bg-slate-200 rounded-full animate-bounce"></div>
                                <div className="w-5 h-5 bg-slate-200 rounded-full animate-bounce [animation-delay:0.2s]"></div>
                                <div className="w-5 h-5 bg-slate-200 rounded-full animate-bounce [animation-delay:0.4s]"></div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {voiceWarning && (
                <div className="fixed bottom-10 right-10 max-w-sm animate-slide-up z-[1001]">
                    <div className="premium-glass bg-rose-50/95 border-4 border-rose-200 p-3 md:p-8 rounded-lg md:rounded-[2.5rem] shadow-2xl flex items-start gap-2 md:p-6 backdrop-blur-3xl">
                        <div className="text-lg md:text-4xl animate-pulse">⚠️</div>
                        <div className="space-y-2">
                            <p className="text-lg font-black text-rose-900 uppercase tracking-tighter">Balsas nerastas</p>
                            <p className="text-sm font-bold text-rose-700 leading-snug">
                                Jūsų naršyklėje nerastas aukštos kokybės angliškas balsas. Geriausiai patirčiai rekomenduojame naudoti "Google Chrome".
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SimonSaysGame;